// requirementsValidator.ts - Funktionen zur Validierung von Übungsanforderungen
import { normalizeInput } from './normalizeUtils';
import { getRotationValue, getTwistValue, checkDuplicates } from './validationUtils';
import { RequirementsData } from './types';

/**
 * Validiert ob die Übungsanforderungen erfüllt werden
 */
export const validateRequirements = (
  exerciseIndex: number,
  exercises: string[][],
  difficultyScores: number[][],
  checkboxes: boolean[],
  selectedRequirement: string,
  requirementsData: RequirementsData
): { [key: string]: boolean } => {
  if (exerciseIndex !== 0 || !selectedRequirement) return {}; // Only validate first exercise if requirement selected
  
  const exercise = exercises[0];
  const normalizedExercise = exercise.map(move => normalizeInput(move));
  
  // Get the requirements for the selected type
  const requirements = requirementsData.exerciseRequirements[selectedRequirement];
  if (!requirements) return {};
  
  const result: { [key: string]: boolean } = {};
  
  // Check if all moves are filled
  const allMovesFilled = exercise.every(move => move.trim() !== '');
  if (!allMovesFilled) return result;
  
  // Check required jumps if specified (AND: all must be present)
  if (requirements.jumps) {
    const normalizedRequiredJumps = requirements.jumps.map(jump => normalizeInput(jump));
    const hasAllRequiredJumps = normalizedRequiredJumps.every(requiredJump =>
      normalizedExercise.some(move => move === requiredJump)
    );
    result['requiredJumps'] = hasAllRequiredJumps;
  }

  // Check optional jump groups (OR within each group: at least one jump per group must be present)
  if (requirements.jumpsOr) {
    requirements.jumpsOr.forEach((group, index) => {
      const normalizedGroup = group.jumps.map(jump => normalizeInput(jump));
      result[`jumpsOr_${index}`] = normalizedGroup.some(requiredJump =>
        normalizedExercise.some(move => move === requiredJump)
      );
    });
  }
  
  // Check for minimum number of saltos (moves with rotation >= 270°)
  if (requirements.less270 !== undefined) {
    const movesWithLessRotation = countMovesWithLessRotation(normalizedExercise);
    result['less270'] = movesWithLessRotation <= requirements.less270;
  }
  
  // Check for required landings (AND logic)
  if (requirements.landingsAnd) {
    const hasAllRequiredLandings = requirements.landingsAnd.every(landing => 
      normalizedExercise.some(move => move.endsWith(landing))
    );
    result['landingsAnd'] = hasAllRequiredLandings;
  }
  
  // Check for required landings (OR logic)
  if (requirements.landingsOr) {
    const hasAnyRequiredLanding = requirements.landingsOr.some(landing => 
      normalizedExercise.some(move => move.endsWith(landing))
    );
    result['landingsOr'] = hasAnyRequiredLanding;
  }
  
  // Check for twist and rotation requirements
  if (requirements.twistRotation) {
    const { leastRotation, leastTwist } = requirements.twistRotation;
    
    // We need to check if there's a move with at least the specified rotation and twist
    const hasTwistRotation = normalizedExercise.some(move => {
      const rotationValue = getRotationValue(move);
      const twistValue = getTwistValue(move);
      return rotationValue >= leastRotation && twistValue >= leastTwist;
    });
    
    result['twistRotation'] = hasTwistRotation;
  }
  
  // Check for jump style requirements
  if (requirements.jumpStyle) {
    const { leastRotation, leastTwist } = requirements.jumpStyle;
    
    // Check for double salto or similar requirements
    const hasJumpStyle = normalizedExercise.some(move => {
      const rotationValue = getRotationValue(move);
      const twistValue = getTwistValue(move);
      return rotationValue >= leastRotation && twistValue >= leastTwist;
    });
    
    result['jumpStyle'] = hasJumpStyle;
  }
  
  // Check for difficulty jumps
  if (requirements.difficultyJumps) {
    const markedDifficultyMoves = checkboxes
      .map((isChecked, index) => isChecked ? difficultyScores[0][index] : 0)
      .filter(score => score > 0);

    result['difficultyJumps'] = markedDifficultyMoves.length >= requirements.difficultyJumps;
  }

  // Check that all jumps are different (no duplicates)
  if (requirements.differentJumps) {
    const duplicates = checkDuplicates(0, exercises);
    result['differentJumps'] = duplicates.every(d => !d);
  }

  // Check rotation + landing requirements (OR logic on landings)
  if (requirements.rotationWithLanding) {
    requirements.rotationWithLanding.forEach((req) => {
      const key = `rotationWithLanding-${req.landings.join('')}-${req.leastRotation}-${req.leastAmount}`;
      const count = normalizedExercise.filter(move => {
        const rotation = getRotationValue(move);
        return rotation >= req.leastRotation && req.landings.some(l => move.endsWith(l));
      }).length;
      result[key] = count >= req.leastAmount;
    });
  }

  return result;
};

/**
 * Helper function to count moves with rotation less than a specific value
 */
function countMovesWithLessRotation(moves: string[]): number {
  return moves.filter(move => {
    const numericPart = move.match(/^[0-9]+/)?.[0] || "";
    
    // This is an example and should be adjusted based on your actual requirements
    // Here we assume the first digit represents full rotations
    const firstDigit = parseInt(numericPart[0] || "0");
    return firstDigit < 3; // Assuming rotation < 270° means first digit < 3
  }).length;
}